// A hub's Toolset flow, from its curator's side: push a toolset, stage it,
// grant the hub access, run its examples and golden questions as recorded
// test runs, ask the hub agent for a repair round, activate it or roll back
// to an earlier one, and list the hub.
// Every step is one Curator API call; the page says in words what the
// Exchange answered. The first listing's approval is the WarmHub team's and
// isn't here. See Design > Flows, "Toolset flow", and Design > Exchange,
// "Activation" and "Rollback".

import { Option, Schema } from "effect";
import type { ComponentChild } from "preact";
import { useEffect, useState } from "preact/hooks";
import { Button, DetailLayout, Dialog, EmptyState, Field, Notice, PageHeader, RailPanel, StatusBadge, TextInput } from "../../../../ui/src/index.ts";
import type { FeedbackTone } from "../../../../ui/src/index.ts";
import { TestRunId, type HubId, type TestRunKind } from "../../../../src/exchange/curator-api.ts";
import { outcomeKey } from "../../domain/activity.ts";
import type { ApiFailure, HubRecord, Toolset } from "../../domain/types.ts";
import { accessView, activateWords, audienceView, blockerRows, grantedWords, historyRows, problemsView, toolsetNote, replacedWords, type BlockerAction, listingView, missesOf, offersRepair, PHASE_WORDS, REPAIR_ASKED, phaseOf, pushCommands, refusalWords, runWords, stageWords, type Change, type ToolsetPhase } from "../../domain/toolsets.ts";
import { activate, grant, listHub, loadActivations, loadToolsets, pushToken, readRun, requestRepair, setAudience, stage, startRun, unlistHub } from "../actions.ts";
import { Failed, Loading, Section } from "../parts.tsx";
import { useHubLabel } from "../hubData.ts";
import { useStore } from "../store.ts";
import { day, dayTime, OUTCOME_WORDS, shortCommit } from "../words.ts";

type Said = { tone: FeedbackTone; text: string } | null;
const PHASE_TONES: Record<ToolsetPhase, FeedbackTone> = { active: "success", staging: "info", rejected: "danger", ready: "success", waiting: "neutral", rollback: "neutral" };
const KIND_WORDS: Record<TestRunKind, string> = { examples: "Examples", golden: "Golden questions" };
// The button that does what a blocker asks, and the change it makes busy while it runs; a blocker with none is waiting or needs a push.
const BLOCKER_BUTTONS: Partial<Record<BlockerAction, { label: string; busy: string }>> = {
  grant: { label: "Grant access", busy: "grant" }, runExamples: { label: "Run the examples", busy: "examples" }, runGolden: { label: "Run the golden questions", busy: "golden" },
};
const TITLES: Record<FeedbackTone, string> = { danger: "That didn't work", info: "Still going", success: "Done", warning: "Check this", neutral: "Note" };
// How often the page reads again while staging or a test run is still going.
const POLL_MS = 5000;
/** The outcome a run recorded, in Studio's words where it has them. */
const outcomeWords = (outcome: string) => { const k = outcomeKey(outcome); return k ? OUTCOME_WORDS[k] : outcome; };

/** Says in words what a change the curator made came to. */
function useSaying() {
  const [said, setSaid] = useState<Said>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** Runs one change: `ok` says what it came to, a refusal is said in words for `change`. */
  const run = async <T,>(label: string, change: Change, call: () => Promise<{ ok: true; value: T } | { ok: false; error: ApiFailure }>, ok: (value: T) => Said) => {
    setBusy(label);
    setSaid(null);
    const r = await call();
    setBusy(null);
    setSaid(r.ok ? ok(r.value) : { tone: "danger", text: refusalWords(change, r.error) });
  };
  return { said, busy, run };
}

const Saying = ({ said }: { said: Said }) => (said ? <Notice live tone={said.tone} title={TITLES[said.tone]}>{said.text}</Notice> : null);

/** Asks before a change that alters what consumers get, naming what it changes. */
function Confirm({ open, title, description, confirm, onConfirm, onClose, children }: {
  open: boolean; title: string; description: string; confirm: string; onConfirm: () => void; onClose: () => void; children?: ComponentChild;
}) {
  return <Dialog open={open} onClose={onClose} title={title} description={description}
    actions={<><Button size="sm" onClick={onClose}>Cancel</Button><Button size="sm" variant="solid" onClick={() => { onClose(); onConfirm(); }}>{confirm}</Button></>}>{children}</Dialog>;
}

const COPY_WORDS = { idle: "Copy", copied: "Copied", failed: "Copy failed: select the text" } satisfies Record<string, string>;

function Command({ label, text }: { label: string; text: string }) {
  const [copied, setCopied] = useState<"copied" | "failed" | null>(null);
  const copy = () => { void navigator.clipboard.writeText(text).then(() => setCopied("copied"), () => setCopied("failed")); };
  return <div class="st-command"><div class="st-command__head"><span class="st-note">{label}</span>
    <Button size="sm" variant="ghost" aria-label={`Copy: ${label}`} onClick={copy}>{COPY_WORDS[copied ?? "idle"]}</Button></div>
    <pre class="st-code">{text}</pre></div>;
}

/** What a push token's response gives the curator to use; the read token for the toolset template is never kept. */
type Pushed = { remote: string; token: string; expiresAt: string };

/** Step 1: a push token for the hub's toolset repository, shown once, with the commands that use it. */
function PushPanel({ record }: { record: HubRecord }) {
  const [pushed, setPushed] = useState<Pushed | null>(null);
  const { said, busy, run } = useSaying();
  const get = () => void run("token", "pushToken", () => pushToken(record.id), ({ remote, token, expiresAt }) => { setPushed({ remote, token, expiresAt }); return null; });
  const cmd = pushed ? pushCommands(pushed.remote) : null;
  return <Section title="1. Push your toolset" note="Your toolset lives in the hub's toolset repository. A push to main stages it; so does Stage below.">
    {!pushed && <Button size="sm" variant="solid" disabled={busy !== null} onClick={get}>{busy === "token" ? "Getting a token…" : "Get a push token"}</Button>}
    <Saying said={said} />
    {pushed && cmd && <div class="st-stack st-commands">
      <Notice tone="info" title="This token is shown once">It lasts until {dayTime(Date.parse(pushed.expiresAt))} and writes to this hub's toolset repository only. Leaving this page forgets it; get another if you need one.</Notice>
      <Command label="Keep the token in your shell, not in a file (paste it, then press Enter)" text={cmd.setup} />
      <Command label="Clone the hub's toolset repository" text={cmd.clone} />
      <Command label="After you edit and commit, push to main" text={cmd.push} />
      <Command label="Your push token" text={pushed.token} />
    </div>}
  </Section>;
}

/** One test run's misses, each with where it failed, its note and the request it was answered under. */
function RunDetail({ hub, runId }: { hub: HubId; runId: TestRunId }) {
  const [load, setLoad] = useState<Awaited<ReturnType<typeof readRun>> | null>(null);
  useEffect(() => {
    let current = true;
    setLoad(null);
    void readRun(hub, runId).then((r) => { if (current) setLoad(r); });
    return () => { current = false; };
  }, [hub, runId]);
  if (load === null) return <p class="st-note" role="status">Loading the results…</p>;
  if (!load.ok) return <Notice tone="danger" title="Studio couldn't read this run">{refusalWords("readRun", load.error)}</Notice>;
  const { results } = load.value;
  if (results === null) return <p class="st-note">This run hasn't recorded results yet.</p>;
  const misses = missesOf(results);
  if (misses.length === 0) return <p class="st-note">All {results.length} questions came back as expected.</p>;
  return <ul class="st-misses" aria-label={`${misses.length} questions that missed`}>{misses.map((m) => <li key={m.question}>
    <strong>{m.question}</strong>
    <span class="st-note">Expected {outcomeWords(m.expectOutcome)}{m.expectIntent ? ` from ${m.expectIntent}` : ""}. Got {outcomeWords(m.outcome)}{m.intent ? ` from ${m.intent}` : ""}{m.stage ? `, failing at ${stageWords(m.stage)}` : ""}.</span>
    {m.note && <span class="st-note">{m.note}</span>}
    {m.requestId && <span class="st-note st-id">Request ID: {m.requestId}</span>}
  </li>)}</ul>;
}

function RunLine({ hub, kind, run }: { hub: HubId; kind: TestRunKind; run: Toolset["runs"]["examples"] }) {
  const [open, setOpen] = useState(false);
  const words = runWords(run, KIND_WORDS[kind]);
  const runId = run === null ? null : Option.getOrNull(Schema.decodeUnknownOption(TestRunId)(run.id));
  const missed = run !== null && run.passed !== null && run.total !== null && run.passed < run.total;
  return <div class="st-runline"><span>{words}</span>
    {runId !== null && run !== null && run.status !== "running" && <Button size="sm" variant="ghost" aria-expanded={open} onClick={() => setOpen(!open)}>{open ? "Hide results" : missed ? "See what missed" : "See results"}</Button>}
    {open && runId !== null && <RunDetail hub={hub} runId={runId} />}</div>;
}

function ToolsetCard({ record, t, active, readOnly }: { record: HubRecord; t: Toolset; active: Toolset | null; readOnly: boolean }) {
  const { said, busy, run } = useSaying();
  const [confirming, setConfirming] = useState(false);
  const phase = phaseOf(t);
  const act = (kind: TestRunKind) => void run(kind, "testRun", () => startRun(record.id, t.commit, kind), (r) => {
    if (r === null) return { tone: "danger", text: "The hub doesn't have that toolset anymore. The list below has been read again." };
    return r.run.status === "running" ? { tone: "info", text: `${KIND_WORDS[kind]} are running. This page checks again on its own.` } : null;
  });
  const grantAccess = () => void run("grant", "grant", () => grant(record.id, t.commit), (v) => {
    const w = grantedWords(v.secret, day);
    return { tone: w.ok ? "success" : "danger", text: w.text };
  });
  const doActivate = () => void run("activate", "activate", () => activate(record.id, t.commit), () => ({ tone: "success", text: "Done." }));
  // The hub agent answers a request for a repair round in an Interaction, later. See Design > Hub agent, "Repair rounds".
  const askRepair = () => void run("repair", "repair", () => requestRepair(record.id, t.commit), (v) => ({ tone: "success", text: REPAIR_ASKED[v.result] }));
  const repair = offersRepair(t.status, readOnly);
  const actions = { grant: grantAccess, runExamples: () => act("examples"), runGolden: () => act("golden"), wait: () => undefined, none: () => undefined } satisfies Record<BlockerAction, () => void>;
  const verb = activateWords(t, active);
  const problems = t.problems.length > 0 ? problemsView(t.problems, dayTime(t.createdAt)) : null;
  // The active toolset has no blockers, so its WarmHub access gets a line of its own. Needs attention's item lands here.
  const access = t.status === "active" ? accessView(t.secret, day) : null;
  return <article class="st-toolset" aria-label={`Toolset ${shortCommit(t.commit)}`}>
    <header class="st-toolset__head">
      <div class="st-stack"><span class="st-id">{shortCommit(t.commit)}</span><span class="st-note">{toolsetNote(t, dayTime(t.createdAt))}</span></div>
      <StatusBadge tone={PHASE_TONES[phase]}>{PHASE_WORDS[phase]}</StatusBadge>
    </header>
    {problems && <Notice tone="danger" title={`${t.problems.length} ${t.problems.length === 1 ? "problem" : "problems"} found at staging`}>
      <ul class="st-problems">{problems.shown.map((p) => <li key={p}>{p}</li>)}</ul>
      {problems.folded.length > 0 && <details class="st-problems__more"><summary>{problems.folded.length} more {problems.folded.length === 1 ? "problem" : "problems"}</summary>
        <ul class="st-problems">{problems.folded.map((p) => <li key={p}>{p}</li>)}</ul></details>}
      {problems.note}</Notice>}
    {t.status !== "rejected" && t.status !== "validating" && <div class="st-runs"><RunLine hub={record.id} kind="examples" run={t.runs.examples} /><RunLine hub={record.id} kind="golden" run={t.runs.golden} /></div>}
    {access && <div class="st-access"><StatusBadge tone={access.tone}>{access.label}</StatusBadge><span class="st-note">{access.text}</span>
      {access.verb && !readOnly && <Button size="sm" variant="solid" disabled={busy !== null} onClick={grantAccess}>{busy === "grant" ? "Working…" : access.verb}</Button>}</div>}
    {t.blockers.length > 0 && <ul class="st-blockers" aria-label="Before this toolset can be activated">{blockerRows(t.blockers).map((w) => {
      const button = BLOCKER_BUTTONS[w.action];
      return <li key={w.tag}><span>{w.text}</span>{button && !readOnly && <Button size="sm" disabled={busy !== null} onClick={actions[w.action]}>{busy === button.busy ? "Working…" : button.label}</Button>}</li>;
    })}</ul>}
    {((t.canActivate && !readOnly) || repair) && <div class="st-actions">
      {t.canActivate && !readOnly && <Button size="sm" variant="solid" disabled={busy !== null} onClick={() => setConfirming(true)}>{busy === "activate" ? "Working…" : verb}</Button>}
      {repair && <Button size="sm" disabled={busy !== null} onClick={askRepair}>{busy === "repair" ? "Asking…" : "Ask for a repair round"}</Button>}
    </div>}
    <Saying said={said} />
    <Confirm open={confirming} onClose={() => setConfirming(false)} onConfirm={doActivate} confirm={verb}
      title={`${verb} ${shortCommit(t.commit)}?`}
      description={active ? `Your hub will answer with ${shortCommit(t.commit)} instead of ${shortCommit(active.commit)}, which goes back to staged.` : `Your hub will answer with ${shortCommit(t.commit)}.`} />
  </article>;
}

/** Each activation of the hub's toolsets, rollbacks included: what became active, what it replaced, and when. The history started on 15 Oct 2026. */
function ActivationHistory({ record }: { record: HubRecord }) {
  const load = useStore((s) => s.activations[record.id]);
  const toolsets = useStore((s) => s.toolsets[record.id]);
  useEffect(() => { void loadActivations(record.id, true); }, [record.id]);
  const active = toolsets?.status === "ok" ? toolsets.value.toolsets.find((t) => t.status === "active") ?? null : null;
  if (!load || load.status === "loading") return null;
  if (load.status === "failed") return <Section title="Activations"><Failed what="the activations" error={load.error} retry={() => void loadActivations(record.id, true)} /></Section>;
  const rows = historyRows(load.value.activations, active);
  if (rows.length === 0) return null;
  return <Section title="Activations" note="Each time a toolset became the one the hub answers with, newest first. The history started being recorded on 15 Oct 2026, so older activations may be missing.">
    <ol class="st-history">{rows.map((r, i) => <li key={i}><span class="st-id">{shortCommit(r.commit)}</span> {dayTime(r.at)}
      <span class="st-note"> {replacedWords(r)}</span>{i === 0 ? <StatusBadge tone="success">Latest</StatusBadge> : null}</li>)}</ol></Section>;
}

/** Step 2: stage `main`, and the toolsets staged so far with what each still needs. The list reads again on its own while staging or a test run is going. */
function ToolsetList({ record, readOnly }: { record: HubRecord; readOnly: boolean }) {
  const load = useStore((s) => s.toolsets[record.id]);
  const { said, busy, run } = useSaying();
  useEffect(() => { void loadToolsets(record.id, true); }, [record.id]);
  const going = load?.status === "ok" && load.value.toolsets.some((t) => t.status === "validating" || t.runs.examples?.status === "running" || t.runs.golden?.status === "running");
  useEffect(() => {
    if (!going) return;
    const timer = setInterval(() => void loadToolsets(record.id, true), POLL_MS);
    return () => clearInterval(timer);
  }, [going, record.id]);
  const stageMain = () => void run("stage", "stage", () => stage(record.id, "main"),
    (r) => (r.toolset === null || r.toolset.status === "validating" ? { tone: "info", text: "Staging is running. This page checks again on its own." } : r.toolset.status === "rejected" ? { tone: "danger", text: "The toolset was rejected. Its problems are listed below." } : { tone: "success", text: `Staged ${shortCommit(r.commit)}.` }));
  let body;
  if (!load || load.status === "loading") body = <Loading label="Loading toolsets" />;
  else if (load.status === "failed") body = <Failed what="the toolsets" notFound="this hub" error={load.error} retry={() => void loadToolsets(record.id, true)} />;
  else if (load.value.toolsets.length === 0) body = <EmptyState>No toolset staged yet. Push one, then stage it.</EmptyState>;
  else {
    const toolsets = load.value.toolsets;
    const active = toolsets.find((t) => t.status === "active") ?? null;
    body = <>
      {/* A card is new when its toolset's status changes, so a message about an earlier change never outlives it. */}
      <div class="st-toolsets">{toolsets.map((t) => <ToolsetCard key={`${t.commit}:${t.status}`} record={record} t={t} active={active} readOnly={readOnly} />)}</div>
      <ActivationHistory record={record} />
    </>;
  }
  return <Section title="2. Stage, test and activate"
    note="Staging checks the toolset. Test runs ask the hub its example and golden questions without charging anyone."
    action={readOnly ? null : <Button size="sm" disabled={busy !== null} onClick={stageMain}>{busy === "stage" ? "Staging…" : "Stage main"}</Button>}>
    <Saying said={said} />
    {body}
  </Section>;
}

/** Where the hub stands with consumers; a curator unlists or lists again once the hub has been listed, and asks again after a first listing wasn't approved. */
function ListingPanel({ record }: { record: HubRecord }) {
  const view = listingView(record);
  const toolsets = useStore((s) => s.toolsets[record.id]);
  const activeCommit = toolsets?.status === "ok" ? toolsets.value.toolsets.find((t) => t.status === "active")?.commit : undefined;
  const { said, busy, run } = useSaying();
  const [unlisting, setUnlisting] = useState(false);
  const [reason, setReason] = useState("");
  const change = (label: "unlist" | "list", call: () => ReturnType<typeof listHub>, text: string) => void run(label, label, call, () => ({ tone: "success", text }));
  const askAgain = () => { if (activeCommit !== undefined) void run("list", "activate", () => activate(record.id, activeCommit), () => ({ tone: "success", text: "Asked again. The WarmHub team will look at it." })); };
  const buttons = {
    unlist: <Button size="sm" disabled={busy !== null} onClick={() => setUnlisting(true)}>Unlist</Button>,
    relist: <Button size="sm" variant="solid" disabled={busy !== null} onClick={() => change("list", () => listHub(record.id), "Listed.")}>List again</Button>,
  } satisfies Record<NonNullable<typeof view.action>, ComponentChild>;
  const rejected = record.listing === "never_listed" && record.listingNote !== null && activeCommit !== undefined;
  return <Section title="Listing" note="Only a listed hub reaches consumers. You can test your hub in every state but archived."
    action={view.action === null ? (rejected ? <Button size="sm" variant="solid" disabled={busy !== null} onClick={askAgain}>Ask for listing again</Button> : null) : buttons[view.action]}>
    <p><StatusBadge tone={view.tone}>{view.label}</StatusBadge> <span class="st-note">{view.text}</span></p>
    <Saying said={said} />
    <Confirm open={unlisting} onClose={() => setUnlisting(false)} confirm="Unlist" title="Unlist this hub?"
      description="Consumers can't reach it until you list it again. Your toolsets stay as they are."
      onConfirm={() => change("unlist", () => unlistHub(record.id, reason.trim() || undefined), "Unlisted.")}>
      <Field id="unlist-reason" label="Reason (optional)" hint="Your hub's other curators see it.">{(attrs) => <TextInput {...attrs} value={reason} onInput={(e) => setReason(e.currentTarget.value)} />}</Field>
    </Confirm>
  </Section>;
}

/** Who can find the hub: everyone signed in, or only the people who can read its repository in WarmHub. See Design > Exchange, "Hub audiences". */
function AudiencePanel({ record }: { record: HubRecord }) {
  const hubs = useStore((s) => s.hubs);
  const blocked = hubs?.status === "ok" ? hubs.value.hubs.find((h) => h.hub.id === record.id)?.audienceBlocked : undefined;
  const view = audienceView(record, blocked);
  const { said, busy, run } = useSaying();
  const [asking, setAsking] = useState(false);
  const [org = "", repo = record.name] = record.name.split("/");
  const limited = record.audience.kind === "org";
  const change = () => void run("audience", "audience", () => setAudience(record.id, limited ? "everyone" : "org"),
    () => ({ tone: "success", text: limited ? "Anyone signed in can find the hub now." : `Only people who can read ${org}/${repo} find the hub now.` }));
  return <Section title="Who can find it"
    note="A hub limited to its org reaches only people who can read its repository in WarmHub. You manage who that is in WarmHub, not here."
    action={record.listing === "archived" ? null : <Button size="sm" disabled={busy !== null} onClick={() => setAsking(true)}>{limited ? "Open to everyone" : `Limit to ${org}`}</Button>}>
    <p><StatusBadge tone={view.tone}>{view.label}</StatusBadge> <span class="st-note">{view.text}</span></p>
    <Saying said={said} />
    <Confirm open={asking} onClose={() => setAsking(false)} confirm={limited ? "Open to everyone" : `Limit to ${org}`}
      title={limited ? "Open this hub to everyone?" : `Limit this hub to ${org}?`}
      description={limited
        ? "Anyone signed in to WarmHub can find and ask the hub once it's listed."
        : `Only people who can read ${org}/${repo} in WarmHub will find and ask the hub; to everyone else it won't exist. The repository must stay private. The hub's listing and the questions it gets still go to the services the Exchange runs on, Jev and turbopuffer.`}
      onConfirm={change} />
  </Section>;
}

export function Toolsets({ record }: { record: HubRecord }) {
  const short = useHubLabel(record.name);
  const archived = record.listing === "archived";
  return <>
    <PageHeader title={<>Toolsets for <strong>{short}</strong></>} description="Push your toolset, test it, and make it the one your hub answers with." />
    <DetailLayout rail={<RailPanel title="How a toolset goes live">
      <p>Push your toolset to the hub's toolset repository, stage it, give the hub access to what it reads, and run the examples and the golden questions. When nothing is left to do, activate it.</p>
      <p class="st-rail-note">An activated toolset is the one the hub answers with. Roll back by activating an earlier toolset: it needs only its access.</p>
      <p class="st-rail-note">After the first activation, the WarmHub team approves your hub's first listing. Later toolsets go live as soon as you activate them.</p>
    </RailPanel>}>
      <ListingPanel record={record} />
      <AudiencePanel record={record} />
      {archived ? <Notice tone="info" title="This hub is archived">Register its repository again to push, stage and activate toolsets. Its toolsets are below.</Notice> : <PushPanel record={record} />}
      <ToolsetList record={record} readOnly={archived} />
    </DetailLayout>
  </>;
}
